// User-preference persistence. A single JSON blob in localStorage captures
// every knob worth remembering across reloads — playback state, tweaks,
// accessibility toggles, and last-played bar. Schema is versioned so we can
// break shape later without blowing up on stale payloads.

import type { Density, PlaybackMode, Theme } from '../music/types';
import type { ChordSize } from '../components/TweaksPanel';

export const STORAGE_KEY = 'guitarguru.prefs.v2';
const MODES: PlaybackMode[] = ['rhythm', 'lead'];
const THEMES: Theme[] = ['classic', 'sketch', 'high-contrast', 'dark'];
const DENSITIES: Density[] = ['compact', 'normal', 'spacious'];
const CHORD_SIZES: ChordSize[] = ['md', 'lg', 'xl', 'xxl'];
const FINGER_ENCODINGS: Preferences['fingerEncoding'][] = ['color', 'pattern'];

export interface Preferences {
  mode: PlaybackMode;
  theme: Theme;
  density: Density;
  chordSize: ChordSize;
  showLyrics: boolean;
  showFingers: boolean;
  showTab: boolean;
  fingerEncoding: 'color' | 'pattern';
  tempoScale: number;
  metronome: boolean;
  voice: boolean;
  synth: boolean;
  countInEnabled: boolean;
  hapticsOn: boolean;
  fingerSonification: boolean;
  loopActive: boolean;
  spotlightOn: boolean;
  lastBar: number;
}

export const DEFAULT_PREFS: Preferences = {
  mode: 'rhythm',
  theme: 'classic',
  density: 'normal',
  chordSize: 'xl',
  showLyrics: true,
  showFingers: true,
  showTab: true,
  fingerEncoding: 'color',
  tempoScale: 1,
  metronome: true,
  voice: true,
  synth: true,
  countInEnabled: true,
  hapticsOn: false,
  fingerSonification: false,
  loopActive: false,
  spotlightOn: false,
  lastBar: 0,
};

function boolOrDefault(value: unknown, fallback: boolean): boolean {
  return typeof value === 'boolean' ? value : fallback;
}

function enumOrDefault<T extends string>(value: unknown, allowed: readonly T[], fallback: T): T {
  return typeof value === 'string' && allowed.includes(value as T) ? value as T : fallback;
}

function numberInRangeOrDefault(value: unknown, min: number, max: number, fallback: number): number {
  if (typeof value !== 'number' || !Number.isFinite(value)) return fallback;
  return Math.max(min, Math.min(max, value));
}

function nonNegativeIntOrDefault(value: unknown, fallback: number): number {
  if (typeof value !== 'number' || !Number.isFinite(value)) return fallback;
  return Math.max(0, Math.floor(value));
}

export function sanitizePreferences(value: unknown): Preferences {
  const raw = typeof value === 'object' && value !== null ? value as Partial<Preferences> : {};
  return {
    mode: enumOrDefault(raw.mode, MODES, DEFAULT_PREFS.mode),
    theme: enumOrDefault(raw.theme, THEMES, DEFAULT_PREFS.theme),
    density: enumOrDefault(raw.density, DENSITIES, DEFAULT_PREFS.density),
    chordSize: enumOrDefault(raw.chordSize, CHORD_SIZES, DEFAULT_PREFS.chordSize),
    showLyrics: boolOrDefault(raw.showLyrics, DEFAULT_PREFS.showLyrics),
    showFingers: boolOrDefault(raw.showFingers, DEFAULT_PREFS.showFingers),
    showTab: boolOrDefault(raw.showTab, DEFAULT_PREFS.showTab),
    fingerEncoding: enumOrDefault(raw.fingerEncoding, FINGER_ENCODINGS, DEFAULT_PREFS.fingerEncoding),
    tempoScale: numberInRangeOrDefault(raw.tempoScale, 0.25, 2, DEFAULT_PREFS.tempoScale),
    metronome: boolOrDefault(raw.metronome, DEFAULT_PREFS.metronome),
    voice: boolOrDefault(raw.voice, DEFAULT_PREFS.voice),
    synth: boolOrDefault(raw.synth, DEFAULT_PREFS.synth),
    countInEnabled: boolOrDefault(raw.countInEnabled, DEFAULT_PREFS.countInEnabled),
    hapticsOn: boolOrDefault(raw.hapticsOn, DEFAULT_PREFS.hapticsOn),
    fingerSonification: boolOrDefault(raw.fingerSonification, DEFAULT_PREFS.fingerSonification),
    loopActive: boolOrDefault(raw.loopActive, DEFAULT_PREFS.loopActive),
    spotlightOn: boolOrDefault(raw.spotlightOn, DEFAULT_PREFS.spotlightOn),
    lastBar: nonNegativeIntOrDefault(raw.lastBar, DEFAULT_PREFS.lastBar),
  };
}

/**
 * Read preferences from localStorage, merging over defaults so fields added
 * in a newer build pick up sane values without a full reset.
 */
export function loadPreferences(): Preferences {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return { ...DEFAULT_PREFS };
    return sanitizePreferences(JSON.parse(raw));
  } catch {
    return { ...DEFAULT_PREFS };
  }
}

/**
 * Merge-save — callers pass only the fields they changed, we overlay on top
 * of the last-saved payload. Failures are swallowed (private mode, quota).
 */
export function savePreferences(patch: Partial<Preferences>): void {
  try {
    const current = loadPreferences();
    const merged = sanitizePreferences({ ...current, ...patch });
    localStorage.setItem(STORAGE_KEY, JSON.stringify(merged));
  } catch {
    /* storage disabled — fail silently */
  }
}

export function resetPreferences(): void {
  try { localStorage.removeItem(STORAGE_KEY); } catch { /* ignore */ }
}
